import TagManager, { TagManagerArgs } from "react-gtm-module";
import { Guid } from "guid-typescript";
import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ToastModel } from "types/ToastModel";
import { ServerErrorResponse } from "results/CommandResult";
import { WindowConfirmProps } from "components/WindowConfirm";
import { Constants } from "utils/Constants";
import { LoginResult } from "results/account/LoginResult";
import { useHistory } from "react-router-dom";
import { RouteList } from "Routes";
import { buildDataLayer, dataTokenKey, getUserData } from "utils/GtmUtils";
import { GlobalDataResult } from "results/account/GlobalDataResult";
import { TeachersApiService } from "services/TeachersApiService";
import { PaymentsApiService } from "services/PaymentsApiService";
import { UsersApiService } from "services/UsersApiService";
import { GamesApiService } from "services/GamesApiService";
import { ContentApiService } from "services/ContentApiService";
import { AccountApiService } from "services/AccountApiService";
import { ReportingApiService } from "services/ReportingApiService";
import { ModerateApiService } from "services/ModerateApiService";
import { GameplayApiService } from "services/GameplayApiService";
import { Moosend } from "utils/MoosendUtils";
import { ClassesApiService } from "services/ClassesApiService";
import { useInitialiseAudio } from "hooks/useInitialiseAudio";
import { HallOfFameApiService } from "services/HallOfFameApiService";
import { DiscoverApiService } from "services/DiscoverApiService";
import { PublishApiService } from "services/PublishApiService";
import { ReportsApiService } from "services/ReportsApiService";
import { ProjectorApiService } from "services/ProjectorApiService";
import { signUpLogInModalId } from "components/modals/SignUpLogInModal";
import { Modal } from "bootstrap";
import { subscribeModalId } from "components/modals/SubscribeModal";

// get username from session
const sessionIdKey = "sessionId";
const sessionIdValue = localStorage.getItem(sessionIdKey);
export const sessionId = sessionIdValue ?? Guid.create().toString();

export const authTokenKey = "auth-v1";

// these are temp fixes to clear out any old user data
const oldAuthTokenKeys = ["auth-token", "data-key", "token"];

for (const key of oldAuthTokenKeys) {
    localStorage.removeItem(key);
}

// get token
export const getToken = () => localStorage.getItem(authTokenKey) ?? "";
export const accessTokenFactory = () => getToken();

// save session id for later just in case
localStorage.setItem(sessionIdKey, sessionId.toString());

// initialise gtm
const tagManagerArgs: TagManagerArgs = {
    gtmId: Constants.gtm,
};

TagManager.initialize(tagManagerArgs);

// initial data layer push
const dataLayer = buildDataLayer();

TagManager.dataLayer({
    dataLayer,
});

// moosend setup
Moosend.init();

// geo location
const continentKey = "continent";
const countryKey = "country";
const isEuKey = "isEu";

// region names for internationalization
const regionNames = new Intl.DisplayNames([navigator.language], { type: "region" });
const regionLanguages = new Intl.DisplayNames([navigator.language], { type: "language" });

export const useGlobalContext = () => {
    const history = useHistory();

    const [isAuthenticated, setIsAuthenticated] = useState<boolean>();
    const [authToken, setAuthToken] = useState<string>();
    const [error, setError] = useState<string>();
    const [notifications, setNotifications] = useState<Array<ToastModel>>([]);
    const [windowConfirm, setWindowConfirm] = useState<WindowConfirmProps>();
    const [userData, setUserData] = useState(getUserData());
    const [globalData, setGlobalData] = useState<GlobalDataResult>();
    const [continent, setContinent] = useState(localStorage.getItem(continentKey));
    const [country, setCountry] = useState(localStorage.getItem(countryKey));
    const [isEu, setIsEu] = useState(
        localStorage.getItem(isEuKey) === undefined ? undefined : localStorage.getItem(isEuKey) === "true",
    );
    const [isGeoLoaded, setIsGeoLoaded] = useState(false);

    // lazy-loaded services cache
    const servicesCache = useRef<Record<string, unknown>>({});

    // helper function to lazily instantiate services
    const getService = useCallback(function <T>(key: string, factory: () => T): T {
        if (!servicesCache.current[key]) {
            servicesCache.current[key] = factory();
        }
        return servicesCache.current[key] as T;
    }, []);

    // create lazy service getters
    const services = useMemo(
        () => ({
            get teachersService() {
                return getService("teachers", TeachersApiService);
            },
            get classesService() {
                return getService("classes", ClassesApiService);
            },
            get paymentsService() {
                return getService("payments", PaymentsApiService);
            },
            get usersService() {
                return getService("users", UsersApiService);
            },
            get gamesService() {
                return getService("games", GamesApiService);
            },
            get contentService() {
                return getService("content", ContentApiService);
            },
            get accountService() {
                return getService("account", AccountApiService);
            },
            get reportingService() {
                return getService("reporting", ReportingApiService);
            },
            get moderateService() {
                return getService("moderate", ModerateApiService);
            },
            get projectorService() {
                return getService("projector", ProjectorApiService);
            },
            get gameplayService() {
                return getService("gameplay", GameplayApiService);
            },
            get hallOfFameService() {
                return getService("hallOfFame", HallOfFameApiService);
            },
            get discoverService() {
                return getService("discover", DiscoverApiService);
            },
            get publishService() {
                return getService("publish", PublishApiService);
            },
            get reportsService() {
                return getService("reports", ReportsApiService);
            },
        }),
        [getService],
    );

    const clearNotifications = useCallback(() => setNotifications([]), []);

    const addNotification = useCallback((toast: ToastModel) => {
        setNotifications(old => [...old, { ...toast, id: old.length }]);
    }, []);

    // globally handle server errors
    const catchServerError = useCallback((e: unknown) => {
        // eslint-disable-next-line no-console
        console.log("Error:");
        // eslint-disable-next-line no-console
        console.log(e);
        setError("An error occurred");
    }, []);

    // update fs auth
    useEffect(() => {
        // the user is considered logged in if there's a token
        const token = localStorage.getItem(authTokenKey) ?? "";

        setIsAuthenticated(!!token);
    }, []);

    const updateAuth = useCallback((data: LoginResult) => {
        // save token
        setAuthToken(data.token);

        // set auth details
        setIsAuthenticated(!!data.token);

        // save
        localStorage.setItem(authTokenKey, data.token);
        localStorage.setItem(dataTokenKey, JSON.stringify(data));
    }, []);

    const updateUserData = () => {
        const data = getUserData();

        setUserData(data);
    };

    const catchFormError = useCallback(
        (response: ServerErrorResponse) => {
            // in the case of a 404 the game has been deleted or closed
            if (response.status === 404) {
                setError("Could not find game");
            }

            if (response.errors) {
                for (const prop in response.errors) {
                    const error = response.errors[prop];

                    addNotification({
                        title: "Oh no!",
                        type: "failure",
                        description: error,
                    });
                }
            }
        },
        [addNotification],
    );

    const logout = useCallback(() => {
        setAuthToken("");
        setIsAuthenticated(false);
        setUserData(undefined);

        localStorage.removeItem(authTokenKey);
        localStorage.removeItem(dataTokenKey);

        history.push(RouteList.Home);
    }, [history]);

    // show sign up / log in modal
    type AccessModalProps = {
        title: string;
    };

    const [signUpModal, setSignUpModal] = useState<AccessModalProps>();
    const [subscribeModal, setSubscribeModal] = useState<AccessModalProps>();

    const showSignUpLogInModal = useCallback((m: AccessModalProps) => {
        const modalElement = document.getElementById(signUpLogInModalId);

        if (modalElement) {
            setSignUpModal(m);

            const modal = Modal.getOrCreateInstance(modalElement);
            modal.show();
        }
    }, []);

    const showSubscribeModal = useCallback((m: AccessModalProps) => {
        const modalElement = document.getElementById(subscribeModalId);

        if (modalElement) {
            setSubscribeModal(m);
            const modal = Modal.getOrCreateInstance(modalElement);
            modal.show();
        }
    }, []);

    // get global user data on initial page load
    useEffect(() => {
        if (isAuthenticated !== undefined && !isGeoLoaded) {
            services.accountService
                .getGlobalUserData()
                .then(x => {
                    // log user out if their account does exist
                    if (isAuthenticated && !x.userExists) {
                        logout();

                        return;
                    }

                    // geo location
                    (async () => {
                        let myCountry = country;
                        let myContinent = continent;
                        let myIsEu = isEu;

                        if (x.ip) {
                            // set a default IP for testing locally
                            const localIps = ["::1", "127.0.0.1"];

                            const ip = localIps.includes(x.ip) ? "124.149.215.70" : x.ip;

                            if (!continent || !country) {
                                const data = await fetch(
                                    `https://api.ipstack.com/${ip}?access_key=f3d3239e74ecfc1eb5c7b5eb9b5645b3`,
                                ).then(
                                    x =>
                                        x.json() as Promise<{
                                            continent_code: string;
                                            country_code: string;
                                            location: {
                                                is_eu: boolean;
                                            };
                                        }>,
                                );

                                if (data.continent_code) {
                                    setContinent(data.continent_code);
                                    localStorage.setItem(continentKey, data.continent_code);
                                    myContinent = data.continent_code;
                                }

                                if (data.country_code) {
                                    setCountry(data.country_code);
                                    localStorage.setItem(countryKey, data.country_code);
                                    myCountry = data.country_code;
                                }

                                if (data.location.is_eu !== undefined) {
                                    setIsEu(data.location.is_eu);
                                    localStorage.setItem(isEuKey, data.location.is_eu ? "true" : "false");
                                    myIsEu = data.location.is_eu;
                                }
                            }
                        }

                        setIsGeoLoaded(true);

                        // gtm
                        if (isAuthenticated) {
                            TagManager.dataLayer({
                                dataLayer: {
                                    userType: x.userType,
                                    accountType: x.accountType,
                                    country: myCountry,
                                    continent: myContinent,
                                    isEu: myIsEu,
                                },
                            });

                            // moosend
                            if (x.email) {
                                Moosend.identify(x.email);
                            }
                        }

                        setGlobalData(x);
                    })();
                })
                .catch(catchServerError);
        }
    }, [catchServerError, continent, country, isAuthenticated, logout, isGeoLoaded, isEu, services.accountService]);

    // if the user is authenticated and geo loaded and doesn't have a country
    // save their country from geolocation
    useEffect(() => {
        if (isGeoLoaded && isAuthenticated && globalData) {
            if (!globalData.country && country) {
                services.usersService.updateProfileLocation({ country }).then(() => {
                    setGlobalData(old => {
                        if (old) {
                            return { ...old, country };
                        }

                        return old;
                    });
                });
            }
        }
    }, [country, globalData, isAuthenticated, isGeoLoaded, services.usersService]);

    // initialise audio
    useInitialiseAudio();

    // isTeacher
    const isTeacher = !!(isAuthenticated && !userData?.isStudent);

    // memoize context value to prevent unnecessary re-renders
    return useMemo(
        () => ({
            ...services, // spread lazy-loaded services
            error,
            setError,
            notifications,
            addNotification,
            catchServerError,
            catchFormError,
            sessionId,
            windowConfirm,
            setWindowConfirm,
            updateAuth,
            logout,
            authToken,
            isAuthenticated,
            clearNotifications,
            userData,
            isTeacher,
            updateUserData,
            globalData,
            setGlobalData,
            country,
            continent,
            isGeoLoaded,
            isEu,
            regionNames,
            regionLanguages,
            showSignUpLogInModal,
            signUpModal,
            showSubscribeModal,
            subscribeModal,
        }),
        [
            services,
            error,
            notifications,
            addNotification,
            catchServerError,
            catchFormError,
            windowConfirm,
            updateAuth,
            logout,
            authToken,
            isAuthenticated,
            clearNotifications,
            userData,
            globalData,
            country,
            isTeacher,
            continent,
            isGeoLoaded,
            isEu,
            showSignUpLogInModal,
            signUpModal,
            showSubscribeModal,
            subscribeModal,
        ],
    );
};

// automatic exports
export type IGlobalContext = ReturnType<typeof useGlobalContext>;
export const GlobalContext = createContext<IGlobalContext>({} as IGlobalContext);

export const GlobalContextProvider = (props: { children: ReactNode }) => {
    const globalContext = useGlobalContext();
    return <GlobalContext.Provider value={globalContext}>{props.children}</GlobalContext.Provider>;
};
